/**
 * Sidebar · RISO_FINAL_DESIGN nav primitive (desktop/tablet side-rail).
 * Per slot_riso_final_ds Phase A1.3 (15 MAY 2026) · ROLE lines 82-89.
 *
 * Renders 4 main nav items + 4 foot items as a fixed-position side rail.
 * Foot now includes Glossary entry (slot_vocabulary_navbar 17 MAY) emitting
 * onNav('glossary') which NavShell intercepts to open GlossaryOverlay.
 * Variant chosen by `device` prop: 'tablet' → rail-fixed (always 60px wide),
 * anything else → rail-hover (collapses to 64px, expands to 220px on hover).
 *
 * Returns null when current screen is in HIDDEN_NAV_SCREENS
 * (login / welcome / finaltest).
 *
 * Atelier nav item (5th overall · NEW per RISO_FINAL_DESIGN umbrella) emits
 * `onNav('atelier')` like any other item. The consumer (NavShell · A1.5)
 * intercepts the 'atelier' key and opens AtelierOverlay (ADR-010) instead
 * of routing to a normal screen.
 *
 * Icon set:
 *   - dashboard · board · sprint · sprints · progress · buddy → migrated from
 *     04_DESIGN/Riso-polished-final/polished_prototype_riso/app.jsx
 *     ProductNav definition (lines 72-127).
 *   - atelier → flask shape (Genie pick Option E, 15 MAY 2026).
 *
 * CSS primitives consumed (see app/src/styles/globals.css migration block
 * "RISO FINAL DESIGN · nav primitives"):
 *   .side-rail · .side-rail.rail-fixed · .side-rail.rail-hover
 *   .rail-mark · .rail-dot · .rail-items · .rail-foot · .rail-buddy
 *   .rail-ic · .rail-lab · button.active
 */

import type { ReactNode, Ref } from 'react';
import { Fragment } from 'react';
import { ThemeToggleWidget } from './ThemeToggleWidget';
import { CourseLink } from './CourseLink';
import { navKeyToRoute } from '@/lib/courseRoute';

export type SidebarDevice = 'desktop' | 'tablet';

export interface SidebarProps {
  /** Current active screen — drives .active class via per-item match list. */
  screen: string;
  /**
   * Device profile.
   *   - 'tablet'  → rail-fixed (always-narrow 60px, no expand)
   *   - 'desktop' → rail-hover (64px collapsed, 220px on hover)
   * NavShell (A1.5) decides which to pass based on viewport width.
   */
  device?: SidebarDevice;
  /** Click handler. Emits item key including 'atelier' for overlay open. */
  onNav: (key: string) => void;
  /**
   * Optional ref attached to the Atelier nav button. NavShell (B.0 ·
   * CF-A1-focus-management) uses it to return focus к the button after
   * AtelierOverlay closes (ESC / re-click / postMessage paths).
   */
  atelierButtonRef?: Ref<HTMLButtonElement>;
}

interface NavItem {
  k: string;
  label: string;
  /** Screen names that mark this item as .active (Sprint → ticket/peek/finaltest). */
  match: ReadonlyArray<string>;
  icon: ReactNode;
  /**
   * Accordion children (slot_homework_rework Ф1b · 17 JUN 2026). When present, the
   * item renders as an expandable group: a head row with a chevron toggles the
   * sub-list; children navigate. Used by «Доска» → Тикеты · Домашка (rail only;
   * BottomTabs renders the parent flat).
   */
  children?: ReadonlyArray<NavItem>;
}

/** 4 main top-bar items · order matches prototype app.jsx lines 72-81. */
const NAV_ITEMS: ReadonlyArray<NavItem> = [
  {
    k: 'dashboard',
    label: 'Сегодня',
    match: ['dashboard'],
    icon: (
      <svg viewBox="0 0 22 22" width="18" height="18" fill="none">
        <circle cx="11" cy="11" r="4.2" stroke="currentColor" strokeWidth="1.6" />
        <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
          <line x1="11" y1="2" x2="11" y2="4" />
          <line x1="11" y1="18" x2="11" y2="20" />
          <line x1="2" y1="11" x2="4" y2="11" />
          <line x1="18" y1="11" x2="20" y2="11" />
          <line x1="4.5" y1="4.5" x2="6" y2="6" />
          <line x1="16" y1="16" x2="17.5" y2="17.5" />
          <line x1="4.5" y1="17.5" x2="6" y2="16" />
          <line x1="16" y1="6" x2="17.5" y2="4.5" />
        </g>
      </svg>
    ),
  },
  // SBM merge (18 MAY 2026 · slot_sprint_board_merge · Genie OQ-6 pick):
  // Former 'board' NAV_ITEM merged into 'sprint' item · label «Доска» (was
  // «Спринт») · icon swapped к 3-col SVG (was play-triangle) · routing key
  // 'sprint' preserved для App.tsx compat. Single nav button for the merged
  // Sprint screen (view toggle inside selects list/board mode).
  //
  // SBM.8 [reopen] (18 MAY 2026 post-review): order swap · «Карта» (sprints)
  // moved BEFORE «Доска» (sprint). Final order: dashboard → sprints → sprint.
  // Rationale: «Карта» = overview courses navigation · «Доска» = active sprint
  // workspace · students plan → enter sprint, so map-then-board reads more
  // naturally left-to-right.
  {
    k: 'sprints',
    label: 'Карта',
    match: ['sprints', 'review'],
    icon: (
      <svg viewBox="0 0 22 22" width="18" height="18" fill="none">
        <path
          d="M3 7 L8 4 L14 7 L19 4 L19 15 L14 18 L8 15 L3 18 Z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <line x1="8" y1="4" x2="8" y2="15" stroke="currentColor" strokeWidth="1.4" />
        <line x1="14" y1="7" x2="14" y2="18" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    ),
  },
  {
    // slot_homework_rework Ф1b (17 JUN 2026 · Genie): «Доска» — раскрывающаяся группа
    // (аккордеон) с детьми Тикеты · Домашка. Клик по голове только раскрывает/сворачивает
    // (доска достаётся через «Тикеты»). Rail-only; BottomTabs рендерит как плоский пункт.
    k: 'sprint',
    label: 'Доска',
    // NB: 'practice' is NOT here — в плоском desktop-layout «Доска» и «Домашка»
    // рендерятся сиблингами, иначе подсветились бы обе на экране практики. Раскрытие
    // аккордеона на практике обеспечивается childActive ниже.
    match: ['sprint', 'ticket', 'peek', 'finaltest', 'board'],
    icon: (
      <svg viewBox="0 0 22 22" width="18" height="18" fill="none">
        <rect x="3" y="4" width="4.5" height="14" stroke="currentColor" strokeWidth="1.6" rx="1" />
        <rect x="9" y="4" width="4.5" height="9" stroke="currentColor" strokeWidth="1.6" rx="1" />
        <rect x="15" y="4" width="4.5" height="11" stroke="currentColor" strokeWidth="1.6" rx="1" />
      </svg>
    ),
    children: [
      {
        k: 'sprint',
        label: 'Тикеты',
        match: ['sprint', 'ticket', 'peek', 'finaltest', 'board'],
        icon: (
          <svg viewBox="0 0 22 22" width="16" height="16" fill="none">
            <rect x="4" y="6" width="14" height="3" stroke="currentColor" strokeWidth="1.6" rx="1" />
            <rect x="4" y="12" width="14" height="3" stroke="currentColor" strokeWidth="1.6" rx="1" />
          </svg>
        ),
      },
      {
        k: 'practice',
        label: 'Практика',
        match: ['practice'],
        icon: (
          <svg viewBox="0 0 22 22" width="16" height="16" fill="none">
            <rect x="5" y="4" width="12" height="15" rx="2" stroke="currentColor" strokeWidth="1.6" />
            <path d="M8.5 4 V3.2 Q8.5 2.4 9.3 2.4 L12.7 2.4 Q13.5 2.4 13.5 3.2 V4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M8 12 L10 14 L14 9.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
      },
    ],
  },
  {
    // Артель — центральная механика курса (маркетплейс-песочница), поэтому в основной
    // группе рядом с Доской/Практикой, а не в нижней вторичной (перенос 9 JUL · Genie).
    // Клик перехватывается NavShell (onNav('atelier') → оверлей); ref для возврата фокуса
    // навешивается в rail-цикле по k==='atelier'.
    k: 'atelier',
    label: 'Артель',
    match: ['atelier'],
    // Flask · Genie pick Option E (15 MAY 2026).
    icon: (
      <svg viewBox="0 0 22 22" width="18" height="18" fill="none">
        <path
          d="M8 3 L8 8 L4 17 Q4 19 6 19 L16 19 Q18 19 18 17 L14 8 L14 3"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <line x1="6.5" y1="3" x2="15.5" y2="3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
];

/**
 * 4 foot items: Glossary (NEW · slot_vocabulary_navbar 17 MAY) ·
 * Progress (existing) · Atelier (5th overall) · Buddy.
 *
 * Glossary item emits onNav('glossary') which NavShell (A1.5) intercepts
 * by calling the lifted openGlossary callback (mirrors atelier intercept
 * pattern). The overlay state itself lives in App.tsx Router (`glossaryOpen`)
 * so the existing Sprint-context CockpitOverview entry stays wired
 * through the same setter.
 */
const NAV_FOOT_ITEMS: ReadonlyArray<NavItem> = [
  {
    k: 'glossary',
    label: 'Глоссарий',
    /** Glossary opens an overlay · no screen state to .active match. */
    match: [],
    // Open-book icon · same 22x22 grammar as sibling foot icons
    // (viewBox + strokeWidth=1.6). Inline rather than reusing
    // src/icons/GlossaryMaster.tsx (designed for 60x60 badge canvas).
    icon: (
      <svg viewBox="0 0 22 22" width="18" height="18" fill="none">
        <path
          d="M4 6 L11 7.5 L18 6 L18 17 L11 18.5 L4 17 Z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <line x1="11" y1="7.5" x2="11" y2="18.5" stroke="currentColor" strokeWidth="1.6" />
        <line x1="6.5" y1="10" x2="9.5" y2="10.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <line x1="6.5" y1="13" x2="9.5" y2="13.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <line x1="12.5" y1="10.4" x2="15.5" y2="10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <line x1="12.5" y1="13.4" x2="15.5" y2="13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    k: 'progress',
    // slot_board_overview P2 (3 JUL 2026 · Genie): «Прогресс» → «Обзор» (bento-
    // дашборд + прежние секции под сгибом). Route-key `progress` не меняется.
    label: 'Обзор',
    match: ['progress'],
    icon: (
      <svg viewBox="0 0 22 22" width="18" height="18" fill="none">
        <path d="M6 14 L6 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M11 14 L11 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M16 14 L16 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="6" cy="12" r="1.6" fill="currentColor" />
        <circle cx="11" cy="8" r="1.6" fill="currentColor" />
        <circle cx="16" cy="5" r="1.6" fill="currentColor" />
        <line x1="3" y1="17" x2="19" y2="17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    k: 'portfolio',
    label: 'Портфолио',
    match: ['portfolio'],
    // Briefcase · slot_homework_rework (cross-course artefact showcase).
    icon: (
      <svg viewBox="0 0 22 22" width="18" height="18" fill="none">
        <rect x="3.5" y="7" width="15" height="11" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path
          d="M8 7 V5.6 Q8 4.6 9 4.6 L13 4.6 Q14 4.6 14 5.6 V7"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <line x1="3.5" y1="12" x2="18.5" y2="12" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    ),
  },
  {
    k: 'buddy',
    label: 'Buddy',
    match: ['buddy'],
    icon: (
      <svg viewBox="0 0 22 22" width="18" height="18" fill="none">
        <ellipse cx="11" cy="12" rx="7" ry="6.5" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="8.5" cy="11" r="0.9" fill="currentColor" />
        <circle cx="13.5" cy="11" r="0.9" fill="currentColor" />
        <path d="M9 14 Q11 15.5 13 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    ),
  },
];

/**
 * Screens that hide the sidebar entirely (auth + final test focus modes).
 *
 * Phase F (16 MAY 2026 · slot_riso_secondary) · 5 auth secondary keys added:
 *   signup · forgot · reset · verify · oauth-callback
 * Matches login behaviour · sidebar hidden during email/password/OAuth flows.
 */
export const HIDDEN_NAV_SCREENS: ReadonlySet<string> = new Set([
  'login',
  'welcome',
  'finaltest',
  'signup',
  'forgot',
  'reset',
  'verify',
  'oauth-callback',
] as const);

const isActive = (item: NavItem, screen: string): boolean => item.match.includes(screen);

export function Sidebar({ screen, device = 'desktop', onNav, atelierButtonRef }: SidebarProps) {
  if (HIDDEN_NAV_SCREENS.has(screen)) return null;

  const variantClass = device === 'tablet' ? 'rail-fixed' : 'rail-hover';

  // open-in-new-tab (28 JUN): render page-section nav items as real <a href> so
  // Ctrl/middle/right-click → new tab works natively; overlay/widget keys
  // (glossary · atelier · buddy) have no route → stay <button>. Left-click on the
  // anchor still does in-app SPA nav (CourseLink intercepts plain clicks).
  const renderNavItem = (
    it: NavItem,
    active: boolean,
    opts?: { ref?: Ref<HTMLButtonElement>; extraCls?: string; key?: string },
  ) => {
    const route = navKeyToRoute(it.k);
    const cls = [active ? 'active' : '', opts?.extraCls].filter(Boolean).join(' ');
    const inner = (
      <>
        <span className="rail-ic">{it.icon}</span>
        <span className="rail-lab">{it.label}</span>
      </>
    );
    if (route) {
      return (
        <CourseLink
          key={opts?.key ?? it.k}
          route={route}
          onNavigate={() => onNav(it.k)}
          className={[cls, 'course-link'].filter(Boolean).join(' ')}
          title={it.label}
          aria-current={active ? 'page' : undefined}
        >
          {inner}
        </CourseLink>
      );
    }
    return (
      <button
        key={opts?.key ?? it.k}
        ref={opts?.ref}
        type="button"
        className={cls}
        onClick={() => onNav(it.k)}
        title={it.label}
        aria-current={active ? 'page' : undefined}
      >
        {inner}
      </button>
    );
  };

  return (
    <nav className={`side-rail ${variantClass}`} aria-label="Навигация">
      <div className="rail-mark">
        <span className="rail-dot">QA</span>
      </div>
      <div className="rail-items">
        {NAV_ITEMS.map((it) => {
          const active = isActive(it, screen);
          // «Доска» group renders FLAT in the side rail: «Доска» (→ board) + «Домашка»
          // (→ practice). The collapsible pop-up menu lives in the mobile bottom-tabs
          // (BottomTabs), not the rail. slot_homework_rework Ф1b · 17 JUN 2026.
          if (it.children && it.children.length > 0) {
            const flat = it.children.filter((c) => c.k !== it.k); // drop dup «Тикеты» (= board)
            return (
              <Fragment key={it.k}>
                {renderNavItem(it, active)}
                {flat.map((ch) =>
                  renderNavItem(ch, isActive(ch, screen), { key: `${it.k}-${ch.label}` }),
                )}
              </Fragment>
            );
          }
          // B.0 · focus-management: ref к Atelier button для возврата фокуса после
          // закрытия оверлея (Артель теперь в основной группе, no route → <button>).
          const ref = it.k === 'atelier' ? atelierButtonRef : undefined;
          return renderNavItem(it, active, { ref });
        })}
      </div>
      <div className="rail-foot">
        {NAV_FOOT_ITEMS.map((it) => {
          const active = isActive(it, screen);
          const extra = it.k === 'buddy' ? 'rail-buddy' : '';
          return renderNavItem(it, active, { extraCls: extra });
        })}
        {/* slot_dark_theme DT.5 (2026-05-18 · Genie pick D1) · 5th foot button.
            Renders outside the NAV_FOOT_ITEMS.map() loop because the widget
            owns its own dynamic icon + click behaviour via useTheme(). It
            inherits the surrounding `.side-rail button` styling so visually
            it is the 5th sibling next к Buddy. */}
        <ThemeToggleWidget />
      </div>
    </nav>
  );
}

/** Re-export raw item lists for BottomTabs (A1.4) reuse — single source of truth. */
export { NAV_ITEMS, NAV_FOOT_ITEMS };
